import { apiCall, apiCallWithCsrfRefresh } from "./api";

const BASE = "/api/notifications";

/**
 * One projected notification row from the durable inbox (Phase 2). Mirrors the
 * server projection in `src/modules/notifications/projectPlanNotifications.ts`:
 * a `fuel_plan.completed` / `fuel_plan.failed` domain event, normalised so the
 * client renders it without any join.
 *
 *   - `type`    — the underlying domain-event kind (completed | failed).
 *   - `outcome` — the resolved three-way state the drain toasts off of:
 *       `live`       — completed AND the plan is still openable → navigate.
 *       `superseded` — completed but the plan was replaced (expired/deleted).
 *       `failed`     — the request itself was unbuildable.
 *     Reading `outcome` directly means the client never re-derives it from
 *     `planStatus` (matches the live watcher's `RequestStatusClass` vocabulary).
 */
export type PlanNotification = {
  id: string;
  type: "completed" | "failed";
  outcome: "live" | "superseded" | "failed";
  requestId: string;
  fuelPlanId: string | null;
  planStatus: string | null;
  failureSummary: string | null;
  label: string | null;
  /** ISO timestamp the event landed at — the drain's watermark clock. */
  at: string;
};

export type NotificationsResponse = {
  /** The principal's current monotonic seen-watermark (ISO), or null pre-cursor. */
  seenAt: string | null;
  notifications: PlanNotification[];
  /** Window-wide count of events newer than `seenAt` (may exceed the list cap). */
  unseenCount: number;
};

/**
 * Load the durable notification inbox for the current principal. Background
 * convenience — callers swallow errors (never break the app on a failed drain).
 */
export async function loadNotifications(signal?: AbortSignal): Promise<NotificationsResponse> {
  return apiCall<NotificationsResponse>(BASE, { signal });
}

/** Default hold the server parks a `/wait` long-poll for (matches the server
 *  cap; kept in sync with `min(holdMs, 20_000)` on the endpoint). */
const DEFAULT_HOLD_MS = 20_000;

/**
 * Bounded long-poll for new notifications (Phase 4 §4.2). GET
 * `/api/notifications/wait?cursor=<iso>&holdMs=<ms>`: the server returns the
 * same projection as `GET /api/notifications` immediately when there are events
 * newer than `cursor`, otherwise parks up to `holdMs` (capped at 20s) and
 * returns `204` on hold expiry.
 *
 * Returns the parsed body on rows, or `null` on a 204 (no content) — `apiCall`
 * already maps a 204 to `undefined`, so an empty hold never throws. Aborts (the
 * loop cancelling an in-flight hold on unmount / idle) propagate as an
 * AbortError; the caller swallows them.
 */
export async function waitForNotifications(
  cursor: string | null,
  opts?: { holdMs?: number; signal?: AbortSignal }
): Promise<NotificationsResponse | null> {
  const holdMs = opts?.holdMs ?? DEFAULT_HOLD_MS;
  const res = await apiCall<NotificationsResponse | undefined>(`${BASE}/wait`, {
    query: { cursor: cursor ?? undefined, holdMs },
    signal: opts?.signal
  });
  // 204 → apiCall returns undefined; normalise to null (no content, loop again).
  return res ?? null;
}

/**
 * Advance the seen-watermark to `seenAt` (monotonic + future-clamped
 * server-side). CSRF POST via the anon+customer principal guard, retried once
 * on a stale-token 403 like every other SPA mutation.
 */
export async function markNotificationsSeen(
  seenAt: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<void> {
  await apiCallWithCsrfRefresh<{ seenAt: string }>(
    `${BASE}/seen`,
    { method: "POST", body: { seenAt }, csrfToken },
    refreshCsrf
  );
}
